import { hotelReservationPhase } from '@bandido/shared/types';
import type {
  HotelReservation,
  HotelReservationRoom,
  HotelReservationTraveler,
  HotelReservationQuote,
} from '@bandido/shared/types';
import type { TravelerResponseDto } from '@bandido/shared/dtos';
import { compareHotelAmounts, type HotelFare } from './searchModel';

export function hotelFareCanReserve(fare: HotelFare, bookingsEnabled = false): boolean {
  return bookingsEnabled && (fare.connector_lineage.connector === 'hotelbeds' || (!!fare.connector_lineage.access_code && !!fare.connector_lineage.supplier_code));
}

/** Only a recent, persisted Book dispatch gets the automatic follow-up window. */
export function hotelBookingFollowUp(reservation: HotelReservation, now: number): 'processing' | 'delayed' | null {
  const snapshot = reservation.hotelSnapshot;
  const started = Date.parse(reservation.bookDispatchedAt ?? '');
  // The clock tick may precede the dispatch, and client/server clocks can differ.
  const elapsed = Math.max(0, now - started);
  if (
    !Number.isFinite(started) ||
    elapsed >= 300000 ||
    !['booking', 'unknown'].includes(snapshot.phase) ||
    snapshot.cancellation ||
    (reservation.refresh && !hotelRefreshPending(reservation))
  ) {
    return null;
  }
  return elapsed < 30000 ? 'processing' : 'delayed';
}

export function hotelReservationPolls(reservation: HotelReservation): boolean {
  return (
    hotelRefreshPending(reservation) ||
    ['quoting', 'booking'].includes(reservation.hotelSnapshot.phase) ||
    ['requested', 'processing'].includes(reservation.hotelSnapshot.cancellation?.status ?? '') ||
    (reservation.hotelSnapshot.cancellation?.status === 'completed' && reservation.hotelSnapshot.phase !== 'cancelled')
  );
}

export function hotelRefreshPending(reservation: HotelReservation): boolean {
  return ['queued', 'claimed', 'retryable_failed'].includes(reservation.refresh?.status ?? '');
}

/** A stale read started before Book cannot restore its submit button. */
export function mergeHotelReservation(current: HotelReservation | null, next: HotelReservation): HotelReservation {
  if (!current || current.id !== next.id) return next;
  if (current.refresh && (!next.refresh || Date.parse(current.refresh.updatedAt) > Date.parse(next.refresh.updatedAt)))
    next = { ...next, refresh: current.refresh };
  const preserved =
    current.refresh === next.refresh && current.refreshUnavailable === next.refreshUnavailable
      ? current
      : { ...current, refresh: next.refresh, refreshUnavailable: next.refreshUnavailable };
  const phase = current.hotelSnapshot.phase;
  if (phase === 'discarded' && ['quoting', 'quoted', 'expired', 'failed'].includes(next.hotelSnapshot.phase)) return preserved;
  if (phase === 'cancelled' && next.hotelSnapshot.phase !== 'cancelled') return preserved;
  const cancellation = current.hotelSnapshot.cancellation;
  if (
    cancellation &&
    (!next.hotelSnapshot.cancellation ||
      (cancellation.id &&
        ['completed', 'failed', 'unknown'].includes(cancellation.status) &&
        ['requested', 'processing'].includes(next.hotelSnapshot.cancellation.status)))
  ) {
    next = { ...next, hotelSnapshot: { ...next.hotelSnapshot, cancellation } };
  }
  if (
    ['confirmed', 'cancelled', 'failed', 'expired'].includes(phase) &&
    ['quoting', 'quoted', 'booking', 'unknown'].includes(next.hotelSnapshot.phase)
  )
    return preserved;
  if (['booking', 'unknown'].includes(phase) && ['quoting', 'quoted'].includes(next.hotelSnapshot.phase))
    return preserved;
  return next;
}

export function hotelReservationCanBook(reservation: HotelReservation, submitted: boolean, now: number): boolean {
  const { phase, quote } = reservation.hotelSnapshot;
  return (
    !submitted &&
    phase === 'quoted' &&
    hotelReservationPhase(reservation.hotelSnapshot, now) === 'quoted' &&
    Boolean(quote?.bookable && quote.acceptedQuote) &&
    (!quote?.expiresAt || (Number.isFinite(Date.parse(quote.expiresAt)) && Date.parse(quote.expiresAt) > now))
  );
}

export interface HotelTravelerSlot {
  roomIndex: number;
  childAge: number | null;
}
export function hotelTravelerSlots(rooms: HotelReservationRoom[]): HotelTravelerSlot[] {
  return rooms.flatMap((room) => [
    ...Array.from({ length: room.adults }, () => ({ roomIndex: room.room_index, childAge: null })),
    ...room.children_ages.map((childAge) => ({ roomIndex: room.room_index, childAge })),
  ]);
}

export function travelerAgeAt(date: string, checkIn: string): number | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || !/^\d{4}-\d{2}-\d{2}$/.test(checkIn)) return null;
  const born = new Date(`${date}T00:00:00Z`);
  const travel = new Date(`${checkIn}T00:00:00Z`);
  if (
    !Number.isFinite(born.getTime()) ||
    !Number.isFinite(travel.getTime()) ||
    born.toISOString().slice(0, 10) !== date ||
    born > travel
  )
    return null;
  return travel.getUTCFullYear() - born.getUTCFullYear() - (checkIn.slice(5) < date.slice(5) ? 1 : 0);
}

/** Use persisted room indices and selected case profiles, never request-order guesses. */
export function hotelBookingTravelers(
  rooms: HotelReservationRoom[],
  ids: string[],
  roster: TravelerResponseDto[],
  checkIn: string,
): HotelReservationTraveler[] | null {
  const slots = hotelTravelerSlots(rooms);
  if (!slots.length || ids.length !== slots.length || new Set(ids).size !== ids.length) return null;
  const travelers: HotelReservationTraveler[] = [];
  for (const [index, slot] of slots.entries()) {
    const traveler = roster.find((person) => person.id === ids[index]);
    if (!traveler?.firstName.trim() || !traveler.lastName.trim()) return null;
    const birth = traveler.birthDate?.slice(0, 10);
    const age = birth ? travelerAgeAt(birth, checkIn) : null;
    // Children require a DOB matching the quoted age; adult DOB remains optional.
    if (slot.childAge !== null && !birth) return null;
    if (birth && (age === null || (slot.childAge === null ? age < 18 : age !== slot.childAge))) return null;
    travelers.push({
      room_index: slot.roomIndex,
      traveler_type: slot.childAge === null ? 'adult' : slot.childAge < 2 ? 'infant' : 'child',
      given_name: traveler.firstName,
      family_name: traveler.lastName,
      ...(birth ? { date_of_birth: birth } : {}),
      travelerId: traveler.id,
    });
  }
  return travelers;
}

/** Only a definite rejection followed by a fresh authoritative read can undo submission.
 * Network failures/timeouts stay ambiguous, even if an earlier GET still says quoted.
 */
export async function recoverHotelBookRejection(
  error: unknown,
  id: string,
  read: (id: string) => Promise<HotelReservation>,
): Promise<{ reservation: HotelReservation; submitted: false; accepted: false } | null> {
  const status = (error as { response?: { status?: number } } | null)?.response?.status;
  if (!status || ![400, 403, 404, 409, 422].includes(status)) return null;
  try {
    const fresh = await read(id);
    return fresh.id === id &&
      fresh.bookDispatchState === 'not_dispatched' &&
      fresh.hotelSnapshot.phase === 'quoted' &&
      !fresh.hotelSnapshot.bookingId
      ? { reservation: fresh, submitted: false, accepted: false }
      : null;
  } catch {
    return null;
  }
}

export function hotelReservationCanDiscard(reservation: HotelReservation): boolean {
  return reservation.canDiscardQuote === true && reservation.bookDispatchState === 'not_dispatched' &&
    !reservation.hotelSnapshot.bookingId && ['quoted', 'expired'].includes(hotelReservationPhase(reservation.hotelSnapshot));
}

export function hotelReservationCanCancel(reservation: HotelReservation): boolean {
  return (
    reservation.hotelSnapshot.phase === 'confirmed' &&
    Boolean(reservation.hotelSnapshot.bookingId) &&
    !reservation.hotelSnapshot.cancellation
  );
}

/** Both normalized and provider commercial terms can disclose payable charges. */
export function hotelQuoteSurcharges(quote: HotelReservationQuote): Record<string, unknown>[] {
  const commercial = quote.commercialTerms?.surcharges;
  return [
    ...quote.surcharges,
    ...(Array.isArray(commercial)
      ? commercial.filter(
          (charge): charge is Record<string, unknown> =>
            !!charge && typeof charge === 'object' && !Array.isArray(charge),
        )
      : []),
  ];
}

/** Quote is internal to Reserve. Continue only when the selected economics are unchanged. */
export function hotelQuoteNeedsReview(fare: HotelFare, reservation: HotelReservation): boolean {
  const quote = reservation.hotelSnapshot.quote;
  if (!quote || quote.priceChanged || quote.cancellationChangedFromSearch !== false) return true;
  if (fare.connector_lineage.connector === 'hotelbeds') {
    const before = fare.connector_lineage.native_conditions, after = quote.nativeConditions;
    // Missing native evidence never proves unchanged conditions. Compare only the
    // safe group projection; Quote remarks are independently reviewed below.
    if (!before?.length || !after?.length ||
      after.some(group => group.allTaxesIncluded === null || group.taxes.some(tax => tax.included === null) ||
        (group.allTaxesIncluded === false && !group.taxes.some(tax => tax.included === false)) ||
        (group.allTaxesIncluded === true && group.taxes.some(tax => tax.included === false))) ||
      JSON.stringify(before) !== JSON.stringify(after)) return true;
  }
  if (fare.total_price.currency !== quote.price.currency_code) return true;
  if (quote.providerBoardCode && fare.room_stays.some((room) => room.board_basis !== quote.providerBoardCode))
    return true;
  try {
    if (compareHotelAmounts(fare.total_price.amount, quote.price.source_amount_text) !== 0) return true;
  } catch {
    return true;
  }
  // Search does not expose or ask consent for these independent selling terms.
  // Matching the total cannot prove binding/MSP or a net/gross breakdown unchanged.
  const commercialPrice = quote.commercialTerms?.price;
  if (commercialPrice && typeof commercialPrice === 'object') {
    const price = commercialPrice as Record<string, unknown>;
    if (price.binding != null && price.binding !== false) return true;
    if (price.minimumSellingPrice != null || price.net != null) return true;
    if (price.gross != null) {
      if (price.currency !== quote.price.currency_code || !['string', 'number'].includes(typeof price.gross))
        return true;
      try {
        if (compareHotelAmounts(String(price.gross), quote.price.source_amount_text) !== 0) return true;
      } catch {
        return true;
      }
    }
  }
  const rules = quote.commercialTerms?.rateRules;
  if (
    Array.isArray(rules) &&
    JSON.stringify([...rules].sort()) !== JSON.stringify([...fare.connector_lineage.rate_rules].sort())
  )
    return true;
  // Newly disclosed remarks/charges must be shown and accepted before Book.
  // Search and Quote use different charge shapes; never assume they are equivalent.
  return quote.remarks.length > 0 || hotelQuoteSurcharges(quote).length > 0;
}

/** Do not render raw transport errors; they may contain credentials or provider payloads. */
export function hotelQuoteErrorKey(error: unknown): string {
  const response = (error as { response?: { status?: number; data?: { code?: string } } })?.response;
  switch (response?.data?.code) {
    case 'HOTEL_BOOKINGS_DISABLED':
      return 'bookingsDisabled';
    case 'HOTEL_FARE_UNAVAILABLE':
      return 'fareUnavailable';
    case 'HOTEL_FARE_LINEAGE_MISSING':
      return 'fareIncomplete';
  }
  switch (response?.status) {
    case 400:
    case 422:
      return 'quoteInvalid';
    case 401:
    case 403:
      return 'quoteForbidden';
    case 404:
      return 'fareUnavailable';
    case 409:
      return 'quoteConflict';
    case 502:
      return 'quoteInvalidResponse';
    case 503:
    case 504:
      return 'quoteUnavailable';
    default:
      return 'quoteConnectionError';
  }
}

/** A lost Quote response can be recovered by the durable attempt UUID, without any POST. */
export async function recoverHotelQuoteAttempt(
  attemptId: string,
  read: (id: string) => Promise<HotelReservation>,
): Promise<HotelReservation | null> {
  try {
    const current = await read(attemptId);
    return current.id === attemptId && current.source === 'hotelrs' ? current : null;
  } catch {
    return null;
  }
}
